import { useMemo } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { capitalize } from '@/utils/capitalize';

/**
 * Hook: Dynamic behavior terminology based on school configuration
 *
 * Returns appropriate labels for positive behaviors ("recognitions") and
 * negative behaviors ("incidents") based on the school's terminology settings.
 *
 * @example
 * const term = useTerminology();
 * // Display dynamic labels
 * <span>{term.incident.pluralCap}</span> // "Incidents" or "Checks" etc.
 * <span>{term.recognition.singularCap}</span> // "Recognition" or "Kudos" etc.
 * // Count-sensitive labels
 * <span>{count} {term.incidentCount(count)}</span> // "5 incidents" or "1 incident"
 */
export function useTerminology() {
  const tenant = useAuthStore((state) => state.tenant);
  const terminology = tenant?.settings?.terminology;

  return useMemo(() => {
    const incidentSingular = terminology?.behavior?.singular || 'incident';
    const incidentPlural = terminology?.behavior?.plural || 'incidents';
    const recognitionSingular = terminology?.recognition?.singular || 'recognition';
    const recognitionPlural = terminology?.recognition?.plural || 'recognitions';
    const pointsSingular = terminology?.points?.singular || 'point';
    const pointsPlural = terminology?.points?.plural || 'points';
    const pointsAbbreviation = terminology?.points?.abbreviation || 'pts';

    return {
      incident: {
        singular: incidentSingular,
        singularCap: capitalize(incidentSingular),
        plural: incidentPlural,
        pluralCap: capitalize(incidentPlural),
      },
      recognition: {
        singular: recognitionSingular,
        singularCap: capitalize(recognitionSingular),
        plural: recognitionPlural,
        pluralCap: capitalize(recognitionPlural),
      },
      points: {
        singular: pointsSingular,
        singularCap: capitalize(pointsSingular),
        plural: pointsPlural,
        pluralCap: capitalize(pointsPlural),
        abbreviation: pointsAbbreviation,
      },
      // Count-sensitive helpers
      incidentCount: (count: number) => (count === 1 ? incidentSingular : incidentPlural),
      recognitionCount: (count: number) => (count === 1 ? recognitionSingular : recognitionPlural),
      incidentCountCap: (count: number) => (count === 1 ? capitalize(incidentSingular) : capitalize(incidentPlural)),
      recognitionCountCap: (count: number) =>
        count === 1 ? capitalize(recognitionSingular) : capitalize(recognitionPlural),
      pointCount: (count: number) => (count === 1 ? pointsSingular : pointsPlural),
      pointCountCap: (count: number) => (count === 1 ? capitalize(pointsSingular) : capitalize(pointsPlural)),
      netScoreLabel: `Net ${capitalize(pointsPlural)}`,
    };
  }, [terminology]);
}
